import { useMemo } from "react";
import type { Command, CommandContext, CreateKind } from "../types";

/**
 * Create commands (S3.10) — context-aware "+ New …" for every primitive that
 * has a reusable standalone modal: agent, folder, task, page, table. Each opens
 * the relevant modal with parent/owner defaulted to the current node (or the
 * workspace root). Memory + task are intentionally absent — they have no
 * standalone create modal yet (created inline from their cockpit panels).
 */

const DEFS: { kind: CreateKind; label: string; icon: string; keywords: string[] }[] = [
  { kind: "agent", label: "New agent", icon: "user-plus", keywords: ["create", "add"] },
  { kind: "folder", label: "New folder", icon: "folder-plus", keywords: ["create", "add"] },
  { kind: "task", label: "New task", icon: "checklist", keywords: ["create", "add", "todo"] },
  { kind: "routine", label: "New routine", icon: "repeat", keywords: ["create", "add", "recurring", "duty"] },
  { kind: "page", label: "New page", icon: "file-plus", keywords: ["create", "add", "doc", "spec"] },
  { kind: "table", label: "New table", icon: "table-plus", keywords: ["create", "add", "data"] },
];

export function useCreateCommands(ctx: CommandContext): Command[] {
  const { openCreate, currentNodeName } = ctx;
  return useMemo(() => {
    const where = currentNodeName ? ` in ${currentNodeName}` : "";
    return DEFS.map<Command>((d) => ({
      id: `create:${d.kind}`,
      label: d.label,
      icon: d.icon,
      kind: "create",
      subtitle: `Create${where}`,
      keywords: d.keywords,
      contextAware: true,
      execute: () => openCreate(d.kind),
    }));
  }, [openCreate, currentNodeName]);
}
